import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { PiopiyProvider } from "./context/PiopiyContext";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import LeadsPage from "./pages/LeadsPage";
import LeadDetailPage from "./pages/LeadDetailPage";
import ChatsPage from "./pages/ChatsPage";
import PerformancePage from "./pages/PerformancePage";
import TeamPage from "./pages/TeamPage";
import SettingsPage from "./pages/SettingsPage";
import UsersPage from "./pages/UsersPage";
import AdminOrdersPage from "./pages/AdminOrdersPage";
import ProformaInvoicesPage from "./pages/ProformaInvoicesPage";
import EmailsPage from "./pages/EmailsPage";
import ReportsPage from "./pages/ReportsPage";
import AiQualityReportPage from "./pages/AiQualityReportPage";
import CustomersPage from "./pages/CustomersPage";
import SharedChatPage from "./pages/SharedChatPage";
import CallsPage from "./pages/CallsPage";
import Customer360Page from "./pages/Customer360Page";
import CampaignsPage from "./pages/CampaignsPage";
import DuplicateTicketsPage from "./pages/DuplicateTicketsPage";
import AiOrdersPage from "./pages/AiOrdersPage";
import CancelUpstreamLogsPage from "./pages/CancelUpstreamLogsPage";
import { LiveCallProvider } from "./contexts/LiveCallContext";
import LiveCallWidget from "./components/LiveCallWidget";
import BrowserCallWidget from "./components/BrowserCallWidget";
import Layout from "./components/Layout";
import { ErrorBoundary } from "./components/ErrorBoundary";
import "@/App.css";

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && user.role !== "super_admin" && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  const { user } = useAuth();

  return (
    <Routes>
      <Route
        path="/login"
        element={user ? <Navigate to="/dashboard" replace /> : <LoginPage />}
      />
      {/* Iter 153.43 — public read-only shared chat (no auth) */}
      <Route path="/share/chat/:token" element={<SharedChatPage />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="chats" element={<ChatsPage />} />
        <Route path="leads" element={<LeadsPage />} />
        <Route path="leads/:leadId" element={<LeadDetailPage />} />
        <Route
          path="performance"
          element={
            <ProtectedRoute allowedRoles={["admin", "leader"]}>
              <PerformancePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="team"
          element={
            <ProtectedRoute allowedRoles={["admin", "leader"]}>
              <TeamPage />
            </ProtectedRoute>
          }
        />
        <Route path="admin-orders" element={<AdminOrdersPage />} />
        <Route path="proforma-invoices" element={<ProformaInvoicesPage />} />
        <Route path="emails" element={<EmailsPage />} />
        <Route path="customers" element={<CustomersPage />} />
        <Route path="customers/:id" element={<CustomersPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="reports/ai-quality" element={<AiQualityReportPage />} />
        <Route path="calls" element={<CallsPage />} />
        <Route path="customer/:phone" element={<Customer360Page />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="users" element={
          <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
            <UsersPage />
          </ProtectedRoute>
        } />
        <Route path="campaigns" element={
          <ProtectedRoute allowedRoles={["super_admin"]}>
            <CampaignsPage />
          </ProtectedRoute>
        } />
        <Route path="admin/ai-orders" element={
          <ProtectedRoute allowedRoles={["super_admin"]}>
            <AiOrdersPage />
          </ProtectedRoute>
        } />
        <Route path="admin/cancel-upstream-logs" element={
          <ProtectedRoute allowedRoles={["admin", "leader", "super_admin"]}>
            <CancelUpstreamLogsPage />
          </ProtectedRoute>
        } />
        <Route path="duplicate-tickets" element={
          <ProtectedRoute allowedRoles={["admin", "leader", "super_admin"]}>
            <DuplicateTicketsPage />
          </ProtectedRoute>
        } />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <PiopiyProvider>
          <LiveCallProvider>
            <ErrorBoundary label="app-root">
              <AppRoutes />
            </ErrorBoundary>
            <LiveCallWidget />
            <BrowserCallWidget />
            <Toaster
              position="top-right"
              toastOptions={{
                style: {
                  background: "#FFF7ED",
                  border: "1px solid #FFEDD5",
                  color: "#1C1917",
                  fontFamily: "Nunito, sans-serif",
                  borderRadius: "14px",
                },
              }}
            />
          </LiveCallProvider>
        </PiopiyProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
